import { notifyWarning } from "@smartsuite/flash-notifications";
import type { EditorView, FilestackExtensionService } from "@smartsuite/smartdoc";
import type {
  AttachmentNodeAttrs,
  FilestackFile,
  ImageNodeAttrs,
  UploadMode,
  VideoNodeAttrs,
} from "@smartsuite/smartdoc/lib/esm/extensions/filestack/types";
import { EFitOptions, type Client as FilestackClient, type PickerFileMetadata } from "filestack-js";
import { AnyFile, filestackMaxSize } from "../../../../config/filestack";
import { AllowedImageExtension, imgSources, VideoExtensions } from "../../../../config/filetypes";
import type { SharedFile, SharedFileInput } from "../../../../types/file";
import { IFRAME_MESSAGES, postMessageToParent } from "../../../../utils/iframeMessages";

export type SaveSharedFiles = (files: SharedFileInput[]) => Promise<SharedFile[]>;

export class FormsFilestackExtensionService implements FilestackExtensionService {
  constructor(
    private readonly filestackClient: FilestackClient,
    private readonly saveFiles: SaveSharedFiles,
  ) {
    this.filestackClient = filestackClient;
    this.saveFiles = saveFiles;
  }

  getAttachmentUrl(attrs: AttachmentNodeAttrs): string {
    if (attrs.url) {
      return attrs.url;
    }

    if (attrs.file?.handle) {
      return this.filestackClient.transform(attrs.file.handle, {});
    }

    return "";
  }

  getImageUrl(attrs: ImageNodeAttrs): string {
    if (attrs.url) {
      return attrs.url;
    }

    if (attrs.file?.handle) {
      return this.filestackClient.transform(attrs.file.handle, {
        resize: { fit: EFitOptions.max, width: 800 },
      });
    }

    return "";
  }

  getVideoUrl(attrs: VideoNodeAttrs): string {
    return attrs?.file?.handle ? this.filestackClient.transform(attrs.file.handle, {}) : "";
  }

  validateUploadingFile(file: File): boolean {
    if (file.size >= filestackMaxSize) {
      notifyWarning(`File ${file.name}: is too big.`);
      return false;
    }

    return true;
  }

  async uploadByUrl(url: string): Promise<FilestackFile> {
    const uploadedFile = await this.filestackClient.storeURL(url);
    const [result] = await this.saveFiles([uploadedFile as SharedFileInput]);
    return result;
  }

  async uploadFile(file: File): Promise<FilestackFile> {
    const uploadedFile: PickerFileMetadata = await this.filestackClient.upload(file);

    const sharedFile = {
      handle: uploadedFile.handle,
      metadata: {
        container: uploadedFile.container ?? "",
        filename: uploadedFile.filename,
        key: uploadedFile.key ?? "",
        mimetype: uploadedFile.mimetype ?? "unknown",
        size: uploadedFile.size,
      },
      transform_options: {},
      security: null,
      created_on: null,
      updated_on: null,
      description: null,
    };

    const [result] = await this.saveFiles([sharedFile]);

    return result;
  }

  uploadSharedFiles(sharedFiles: SharedFileInput[]): Promise<FilestackFile[]> {
    if (sharedFiles.length > 0) {
      return this.saveFiles(sharedFiles) as Promise<FilestackFile[]>;
    }
    return Promise.reject(new Error("No files to upload"));
  }

  async uploadPickerFilesAndResolve(files: PickerFileMetadata[]): Promise<FilestackFile[]> {
    const sharedFiles: SharedFileInput[] = files.map((file) => ({
      handle: file.handle,
      metadata: {
        container: file.container ?? "",
        filename: file.filename,
        key: file.key ?? "",
        mimetype: file.mimetype ?? "unknown",
        size: file.size,
      },
    }));

    return await this.uploadSharedFiles(sharedFiles);
  }

  openPicker(mode: UploadMode): Promise<FilestackFile[]> {
    return new Promise<FilestackFile[]>((resolve, reject) => {
      const acceptMap = {
        video: VideoExtensions,
        image: AllowedImageExtension,
        default: AnyFile,
      };
      const accept = acceptMap[mode as keyof typeof acceptMap] ?? acceptMap.default;

      const picker = this.filestackClient.picker({
        accept,
        fromSources: imgSources,
        maxFiles: 100,
        disableTransformer: true,
        onClose: () => postMessageToParent(IFRAME_MESSAGES.MODAL_CLOSED),
        onUploadDone: (response) => {
          this.uploadPickerFilesAndResolve(response.filesUploaded).then(resolve).catch(reject);
        },
      });

      postMessageToParent(IFRAME_MESSAGES.MODAL_OPENED);
      void picker.open();
    });
  }

  openModalGallery(view: EditorView, attrs: ImageNodeAttrs): void {
    // TODO: implement gallery modal if needed
  }
}
